'use client'

import { useState } from 'react'
import { cn } from '../lib/cn'
import { trackEvent } from '../lib/analytics'

interface FAQItem {
  question: string
  answer: string
}

interface FAQProps {
  items: FAQItem[]
  className?: string
}

export function FAQ({ items, className }: FAQProps) {
  // Track a set of open indices so multiple items can stay open
  const [openSet, setOpenSet] = useState<Set<number>>(new Set())

  const toggleItem = (index: number) => {
    setOpenSet(prev => {
      const next = new Set(prev)
      const isCurrentlyOpen = next.has(index)
      
      if (isCurrentlyOpen) {
        next.delete(index)
      } else {
        next.add(index)
      }
      
      // Track FAQ click event in Google Analytics
      trackEvent(
        isCurrentlyOpen ? 'faq_collapse' : 'faq_expand',
        'FAQ',
        items[index].question
      )
      
      return next
    })
  }

  return (
    <div className={cn('w-full max-w-4xl mx-auto', className)}>
      <div className="space-y-4">
        {items.map((item, index) => {
          const isOpen = openSet.has(index)
          
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-200 hover:shadow-md"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none rounded-xl"
              >
                <h3 className="text-h4 font-display text-night pr-4">
                  {item.question}
                </h3>
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center transition-colors duration-200 hover:bg-gray-200">
                    {isOpen ? (
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </button>
              
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                <div className="px-6 pb-5 pt-2">
                  <p className="text-gray-600 leading-relaxed text-left font-normal">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
